/** Session flag: show post-registration walkthrough after next successful login. */
export const ONBOARDING_SESSION_KEY = 'crud:show-onboarding';

/** Cookie survives payment-success localStorage.clear() before login. */
export const ONBOARDING_COOKIE = 'crud_onboarding_pending';

const onboardingCompleteKey = (userId: number) => `crud:onboarding-complete:${userId}`;
const proxySetupCompleteKey = (userId: number) => `crud:proxy-setup-complete:${userId}`;
const featureTourCompleteKey = (userId: number) => `crud:feature-tour-complete:${userId}`;

export function markOnboardingPendingForNextLogin(): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${ONBOARDING_COOKIE}=1; Max-Age=86400; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* ignore */
  }
}

export function captureOnboardingFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('onboarding') === '1') {
      sessionStorage.setItem(ONBOARDING_SESSION_KEY, '1');
    }
    const hasCookie = document.cookie
      .split('; ')
      .some((part) => part.trim() === `${ONBOARDING_COOKIE}=1`);
    if (hasCookie) {
      sessionStorage.setItem(ONBOARDING_SESSION_KEY, '1');
      document.cookie = `${ONBOARDING_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
    }
  } catch {
    /* ignore */
  }
}

export function isOnboardingSessionActive(): boolean {
  try {
    return sessionStorage.getItem(ONBOARDING_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearOnboardingSession(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function markOnboardingComplete(userId: number): void {
  try {
    localStorage.setItem(onboardingCompleteKey(userId), '1');
    localStorage.setItem(featureTourCompleteKey(userId), '1');
    clearOnboardingSession();
  } catch {
    /* ignore */
  }
}

export function markProxySetupComplete(userId: number): void {
  try {
    localStorage.setItem(proxySetupCompleteKey(userId), '1');
  } catch {
    /* ignore */
  }
}

/** Clear when proxy is no longer available so setup overview can prompt again. */
export function clearProxySetupComplete(userId: number): void {
  try {
    localStorage.removeItem(proxySetupCompleteKey(userId));
  } catch {
    /* ignore */
  }
}

export function hasCompletedProxySetup(userId: number): boolean {
  try {
    return localStorage.getItem(proxySetupCompleteKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markFeatureTourComplete(userId: number): void {
  markOnboardingComplete(userId);
}

export function hasCompletedFeatureTour(userId: number): boolean {
  try {
    return (
      localStorage.getItem(featureTourCompleteKey(userId)) === '1' ||
      localStorage.getItem(onboardingCompleteKey(userId)) === '1'
    );
  } catch {
    return false;
  }
}

export function hasCompletedOnboarding(userId: number): boolean {
  try {
    return localStorage.getItem(onboardingCompleteKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function shouldShowPostRegistrationWalkthrough(userId: number | undefined): boolean {
  if (!userId) return false;
  if (hasCompletedProxySetup(userId)) return false;
  // New accounts only (registration login with onboarding session).
  return isOnboardingSessionActive();
}

/** Product feature tour — after proxy setup, until user finishes or skips. */
export function shouldShowFeatureTour(userId: number | undefined): boolean {
  if (!userId) return false;
  if (hasCompletedFeatureTour(userId)) return false;
  return hasCompletedProxySetup(userId);
}
